import { openSharedEventStream } from "./sharedEventStream";
import { parseSseFrame } from "./sseFrame";
import { FRAME_TERMINATOR_LENGTH, INITIAL_RETRY_MS, MAX_RETRY_MS, RETRY_BACKOFF_FACTOR, STARTUP_SCOPE, TRANSPORT_EVENT_RECONNECTED, TRANSPORT_EVENT_RECONNECTING } from "./eventTransport";

/** EventSource surface backed by the same broker as authenticated live streams. */
export function createSharedEventSource(url: string): Pick<EventTarget, "addEventListener" | "removeEventListener"> & { close: () => void } {
  const target = new EventTarget();
  const abort = new AbortController();
  let retry = INITIAL_RETRY_MS;
  const run = async () => {
    while (!abort.signal.aborted) {
      try {
        const response = await openSharedEventStream(url, { signal: abort.signal, scope: STARTUP_SCOPE, transportEvents: true });
        if (!response.ok || !response.body) throw new Error("Startup event stream unavailable");
        retry = INITIAL_RETRY_MS;
        target.dispatchEvent(new Event("open"));
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          buffer = buffer.replace(/\r\n/g, "\n");
          let boundary: number;
          while ((boundary = buffer.indexOf("\n\n")) >= 0) {
            const frame = parseSseFrame(buffer.slice(0, boundary));
            buffer = buffer.slice(boundary + FRAME_TERMINATOR_LENGTH);
            if (!frame) continue;
            if (frame.eventType === TRANSPORT_EVENT_RECONNECTING) target.dispatchEvent(new Event("error"));
            else if (frame.eventType === TRANSPORT_EVENT_RECONNECTED) target.dispatchEvent(new Event("open"));
            else target.dispatchEvent(new MessageEvent(frame.eventType, { data: frame.data }));
          }
        }
      } catch {
        if (abort.signal.aborted) break;
      }
      if (abort.signal.aborted) break;
      target.dispatchEvent(new Event("error"));
      await new Promise<void>(resolve => {
        const done = () => { window.clearTimeout(timer); abort.signal.removeEventListener("abort", done); resolve(); };
        const timer = window.setTimeout(done, retry);
        abort.signal.addEventListener("abort", done, { once: true });
      });
      retry = Math.min(retry * RETRY_BACKOFF_FACTOR, MAX_RETRY_MS);
    }
  };
  void run();
  return { addEventListener: target.addEventListener.bind(target), removeEventListener: target.removeEventListener.bind(target), close: () => abort.abort() };
}
